import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, MessageCircle, Heart, Shield, Users } from 'lucide-react';

const Login: React.FC = () => {
  const [userid, setUserid] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const success = await login(userid, password);
    if (success) {
      navigate('/doctor-dashboard');
    } else {
      setError('Invalid credentials. Please try again.');
    }
  };

  const demoCredentials = {
    doctor: { userid: 'user001', password: 'doctor123' }
  };

  const fillDemoCredentials = () => {
    setUserid(demoCredentials.doctor.userid);
    setPassword(demoCredentials.doctor.password);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left side - Phone mockup with content */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-8">
        <div className="relative">
          {/* Phone mockup background */}
          <div className="relative bg-black rounded-3xl p-2 shadow-2xl" style={{ width: '350px', height: '700px' }}>
            <div className="bg-white rounded-2xl h-full overflow-hidden relative">
              {/* Instagram-like header */}
              <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
                <h1 className="text-2xl font-bold">Traveller app</h1>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                </div>
              </div>
              
              {/* Sidebar */}
              <div className="flex h-full">
                <div className="w-16 bg-white border-r flex flex-col items-center py-6 space-y-6">
                  <div className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                  </div>
                  <div className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                    <MessageCircle className="w-6 h-6" />
                  </div>
                </div>
                
                {/* Main content area */}
                <div className="flex-1 bg-white">
                  {/* Post header */}
                  <div className="p-3 border-b flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                      <Heart className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">travellerapp</p>
                      <p className="text-xs text-gray-500">30m</p>
                    </div>
                  </div>
                  
                  {/* Post image */}
                  <div className="bg-gradient-to-br from-blue-400 to-purple-500 h-48 flex items-center justify-center">
                    <div className="text-white text-center">
                      <Heart className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm font-medium">Discover</p>
                      <p className="text-sm font-medium">Amazing Destinations</p>
                    </div>
                  </div>
                  
                  {/* Post content */}
                  <div className="p-3">
                    <p className="font-bold text-sm text-blue-600 mb-1">EXPLORING WORLDS, ONE TRIP AT A TIME</p>
                    <p className="text-xs text-gray-600">Join thousands of travelers sharing tips and itineraries...</p>
                  </div>
                </div>
                
                {/* Right sidebar with suggestions */}
                <div className="w-20 bg-white border-l p-2">
                  <div className="text-xs font-semibold text-gray-600 mb-3 text-center">Suggested</div>
                  <div className="space-y-3">
                    {['Emily', 'Michael', 'Sarah', 'James', 'Lisa'].map((name, index) => (
                      <div key={index} className="text-center">
                        <div className="w-8 h-8 bg-gray-300 rounded-full mx-auto mb-1"></div>
                        <p className="text-xs text-gray-600 truncate">{name}</p>
                        <button className="text-xs text-blue-500 font-medium">Follow</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Messages indicator */}
              <div className="absolute bottom-4 right-4 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full p-2 shadow-lg">
                <MessageCircle className="w-4 h-4 text-white" />
                <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">3</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="max-w-sm w-full space-y-8">
          {/* Logo and title */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-4 w-16 h-16 mx-auto flex items-center justify-center mb-6">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Traveller app</h1>
            <p className="text-gray-600">Plan trips and connect with fellow travelers instantly</p>
          </div>

          {/* Login Form */}
          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <form className="space-y-4" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <input
                  id="userid"
                  name="userid"
                  type="text"
                  autoComplete="username"
                  required
                  value={userid}
                  onChange={(e) => setUserid(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:outline-none focus:border-gray-400 focus:bg-white"
                  placeholder="User ID"
                />
              </div>

              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:outline-none focus:border-gray-400 focus:bg-white pr-10"
                  placeholder="Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-blue-600 hover:text-blue-500"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>

              {/* Demo Credentials */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">Demo Credentials:</p>
                <button
                  type="button"
                  onClick={fillDemoCredentials}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Use Traveller demo account
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
                </div>
              </div>

              <div className="mt-4">
                <Link
                  to="/register"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Create new account
                </Link>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="bg-green-100 p-3 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <Heart className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-xs text-gray-600">24/7 Care</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 p-3 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-xs text-gray-600">Secure</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 p-3 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <p className="text-xs text-gray-600">Expert Guides</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;